import React, { useEffect, useState } from "react";
import { DASHBOARD } from "src/utils/constants/routes";
import { useFormik } from "formik";
import { string, object, number } from "yup";
import { useHistory } from "react-router-dom";
import { Layout } from "src/components/Layout";
import { Wrapper } from "./styled";
import {
  Banner,
  Withdraws,
  Select,
  Form,
  PageDetails,
  Icon,
  Button,
} from "src/components";
import { useCustomInvestments } from "src/store/hooks/useInvestements";

import ArrowCircleright from "src/assets/ArrowCircleright.svg";
import { formatAmount } from "src/utils/number";
import { ValueDetails } from "./InvestAmountDetails/details";
import { SMALL_LAPTOP } from "src/utils/constants/media";
import { useWindowSize } from "src/hooks/useWindowSize";
import FWithdraw from "./FWithdraw";
import { useTheme } from "styled-components";
import { useGetBankAccountsQuery } from "src/store/bankAccounts/api";
import {
  useSendWithdrawOtpRequestMutation,
  useVerifyOTPMutation,
} from "src/store/withdraws/api";
import NiceModal from "@ebay/nice-modal-react";
import Otp from "src/components/Modals/Otp";
import Success from "src/components/Modals/Success";
import triggerSuccessIcon from "src/assets/trigger-success.svg";
import { message } from "antd";
import styled from "styled-components";
import PageHeader from "src/components/PageHeader";
import { Button as UIButton, DialogFooter } from "src/components/ui";
import { WITHDRAW_FORM_ID } from "./formId";
import {
  AccountField,
  AccountRow,
} from "src/components/Modals/CashFlows/AccountRow";
import FDeposit from "src/components/FDeposit";
import { ManualBankModel } from "src/pages/LeanDeposit/ManualBank";
import { PlusSignCircleIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { useClient } from "src/store/hooks";
import { WITHDRAW_CASH_MODAL } from "src/components/Modals/CashFlows/ids";

/* A flex ITEM in the app's content row, so it has to be told to take the
   row: at its default `flex: 0 1 auto` a width of 100% resolves against its
   own content, and these pages were rendering in a 540px column with four
   hundred pixels of the window unused beside them. */
/* The form takes the dialog's own measure — see the note on the deposit
   dialog's body. */
const DialogBody = styled.div`
  width: 100%;
`;

const Page = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0;
`;

/* The floor, from the environment — quoted in the message and used as the
   form's opening figure rather than written out again in either place. */
const MINIMUM_WITHDRAWAL = Number(
  process.env.REACT_APP_min_withdraw_cash || "100",
);

/**
 * @param asDialog Rendered inside a dialog rather than as a page — the same
 *   form, the same validation and the same OTP step, with the page header,
 *   the page-width card and the balance column replaced by the dialog's own
 *   chrome and a balance strip above the fields.
 */
/* Either the dialog's foot or nothing at all — a page renders the pair in
   its own flow, which is what the fragment does. */
const Slot: React.FC<{ asDialog?: boolean; children: React.ReactNode }> = ({
  asDialog,
  children,
}) => (asDialog ? <DialogFooter>{children}</DialogFooter> : <>{children}</>);

const WithdrawContainer = ({
  asDialog,
  onClose,
}: {
  asDialog?: boolean;
  onClose?: () => void;
} = {}) => {
  const {
    data: bankAccounts,
    isLoading,
    isFetching: banksFetching,
    refetch: refetchBanks,
  } = useGetBankAccountsQuery("");
  const [
    sendWithdrawOtpRequest,
    { isLoading: sendWithdrawOtpRequestIsLoading },
  ] = useSendWithdrawOtpRequestMutation();
  const [verifyOTP] = useVerifyOTPMutation();
  const size = useWindowSize();
  /* Adding an account is the same dialog the deposit flow opens — the same
     one, not a variant of it. */
  const [addingBank, setAddingBank] = useState(false);
  const { data: clientData } = useClient();
  const { data, isFetching: balancesFetching } = useCustomInvestments();
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);

  /**
   * What can actually leave the account.
   *
   * The panel above these fields has always shown three figures — total cash,
   * what is reserved, and the difference — and the form was validating
   * against the FIRST of them. So with $4,523 in total and $4,491 reserved,
   * the panel said $32 was available to withdraw and the field beside it
   * accepted four thousand: the reader was told one number and bounded by
   * another, and the request would only fail later.
   *
   * Not a new figure — it is the same subtraction the panel already performs
   * and displays, used as the bound it was always describing.
   */
  const availableToWithdraw =
    Number(data?.totalCash ?? 0) - Number(data?.reservedCash ?? 0);

  const formik = useFormik({
    initialValues: {
      /* The form opens at the smallest withdrawal it will accept, as the
         deposit form opens at its own minimum: a figure the reader can
         adjust rather than an empty field with the floor hidden inside the
         validator. */
      amount: MINIMUM_WITHDRAWAL,
      destination: "",
      comment: "",
      provider: "manual",
    },
    onSubmit: async (values) => {
      /* The result is READ.
         This was `sendWithdrawOtpRequest(values).then(() => show(Otp))`, and
         an RTK Query mutation resolves with `{ error }` rather than
         rejecting — so when the send failed the flow went on to ask for a
         code that had never been sent. On intg it fails every time (the
         gateway answers 403 from its own OTP service), and the reader met a
         code box that could only ever reject them. */
      const sent: any = await sendWithdrawOtpRequest(values);
      if (sent?.error) {
        message.error(
          "We couldn't send your code just now. Please try again in a moment.",
        );
        return;
      }
      /* One dialog at a time.
         The code step opened ON TOP of this one, so the reader met two sheets
         stacked with two scrims between them and the page — the withdraw form
         still fully drawn behind a smaller box asking for six digits. A flow
         should advance, not pile up.

         `remove`, not `hide`. Hiding sets `visible` false (confirmed: the
         handle's own `visible` does flip), but this dialog's `afterClose`
         calls `remove()` on the same modal, and the two raced: the wrap never
         reached `display: none` and the form stayed on screen over the code
         box. Removing it outright skips the close animation and the race. */
      if (asDialog) NiceModal.remove(WITHDRAW_CASH_MODAL);
      NiceModal.show(Otp, {
        onSuccess: async () => {
          /* `successMessage`, not `title`. This dialog renders the line that
             says what happened from `successMessage`; `title` is its head.
             Passed as `title` the head read "Withdrawal Successful" and the
             line under the icon — the one the whole dialog is built around —
             rendered empty, leaving a blank band in the middle of the last
             screen of the flow. The six other callers all pass
             `successMessage`. */
          NiceModal.show(Success, {
            icon: triggerSuccessIcon,
            successMessage: "Withdrawal Successful",
            definition:
              "Your withdrawal request has been successfully processed.",
          });
        },
        verify: async (otp) => {
          return verifyOTP({ otpCode: otp, ...values }).unwrap();
        },
        /* The reader is told what failed, not just that something did. The
           dialog stays open so the code can be tried again. */
        onFailure: async () => {
          message.error("We couldn't place your withdrawal. Please try again.");
        },
        resend: async () => {
          return sendWithdrawOtpRequest(values);
        },
      });
    },
    validate: async (values) => {
      try {
        await object({
          /* Messages a reader can act on.
             An emptied field said "Must be a number", which describes the
             library's cast rather than the mistake; the floor quoted a
             hardcoded 100 while the bound itself came from the environment;
             and the ceiling said "More than is available to withdraw"
             without saying what that is, so the reader had to go back up to
             the strip to find out. */
          amount: number()
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .typeError("Enter the amount in figures")
            .min(
              MINIMUM_WITHDRAWAL,
              `The smallest withdrawal is $${formatAmount(MINIMUM_WITHDRAWAL)}`,
            )
            .max(
              availableToWithdraw,
              `You can withdraw up to $${formatAmount(availableToWithdraw)}`,
            )
            .required("Enter the amount you want to withdraw"),
          destination: string().required(
            "Choose the account to send the money to",
          ),
          comment: string(),
          provider: string(),
        }).validate(values, { abortEarly: false });
        return {};
      } catch (err) {
        const errors = {};
        const { inner } = err;
        inner.forEach((e) => (errors[e.path] = e.message));
        return errors;
      }
    },
  });

  const feedback = (field) => {
    const { errors, touched } = formik;
    return errors[field] && touched[field] ? errors[field] : null;
  };

  /* Not `dirty`: the form arrives with an account chosen and an amount in
     it, so "has the reader typed something" stopped being the question. What
     has to hold is that the figure is within its bounds and an account is
     selected. */
  const isSubmitAllowed =
    formik.values.amount >= MINIMUM_WITHDRAWAL &&
    formik.values.amount <= availableToWithdraw &&
    Boolean(formik.values.destination);
  const onSelectionChange = (value, name) => {
    formik.setFieldValue(name, value);
  };

  /**
   * The first account, chosen for them.
   *
   * Almost everyone has exactly one, and the form then opened on an empty
   * select that had to be opened, read and picked from before anything else
   * could be filled in — a required choice with one option in it. Set once,
   * and never over a choice the reader has already made.
   */
  useEffect(() => {
    if (!bankAccounts?.length || formik.values.destination) return;
    const first = bankAccounts[0];
    /* One update: two setFieldValue calls validate against two different
       snapshots, and the second one's can be the one that sticks. */
    formik.setValues({
      ...formik.values,
      destination: first.value,
      provider: first.provider || "manual",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts]);

  useEffect(() => {
    if (!isDetailsVisible && size?.width < SMALL_LAPTOP) {
      setIsDetailsVisible(true);
    } else if (isDetailsVisible && size?.width > SMALL_LAPTOP) {
      setIsDetailsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.width]);

  /**
   * Whether anything can be withdrawn at all.
   *
   * A real state, and a common one: most of a balance can be reserved
   * against open investments, leaving less than the smallest withdrawal the
   * product accepts. Left to the field's own validation the reader met it as
   * a red line under an amount they had not typed — which reads as their
   * mistake — or, untouched, as a dead Withdraw button with no explanation
   * anywhere. It is a condition of the account, so it is stated at the top
   * in place of the note about wire timing, which cannot apply.
   */
  const canWithdraw = availableToWithdraw >= MINIMUM_WITHDRAWAL;

  const amount = formatAmount(formik.values.amount);
  const total = formatAmount(data?.totalCash);
  const newTotal = formatAmount(data?.totalCash - formik.values.amount);
  const history = useHistory();
  const theme = useTheme();
  /* No spinner over the whole dialog.
     The figures come from the dashboard's own query cache — the same key the
     overview page fills — so they are on screen the moment this opens, and
     replacing the body with a spinner threw away numbers the reader had just
     been looking at to show them nothing. Only the account list is genuinely
     unknown on a cold open, so only the account list waits, and the strip
     says when it is checking the balances again. */
  const body = (
    <Withdraws>
      <FWithdraw leanWrapper={true} $full={asDialog} title="Withdraw Cash">
        {canWithdraw ? (
          <Banner>
            {process.env.REACT_APP_BANK_NAME || ""} Capital will wire this
            amount in the coming hours to your original bank account. It should
            reach it within two business days. Kindly advise us otherwise.
          </Banner>
        ) : (
          <Banner tone="caution" lead="You can't withdraw right now">
            {/* The reserved sentence only holds when something is reserved.
                It was appended whatever the figures were, so an account with
                nothing in it read "The rest of your cash, $0, is reserved for
                your open investments" — explaining a reservation that does
                not exist, against investments there are none of. */}
            {`You have $${formatAmount(
              availableToWithdraw,
            )} available, and the smallest withdrawal is $${formatAmount(
              MINIMUM_WITHDRAWAL,
            )}.${
              data?.reservedCash > 0
                ? ` The rest of your cash, $${formatAmount(
                    data?.reservedCash,
                  )}, is reserved for your open investments. It frees up when they settle or you exit them.`
                : ""
            }`}
          </Banner>
        )}

        {/* The narrow-screen copy of the balance panel, which only the PAGE
            needs: below the layout's breakpoint the page hides its side
            column and shows the figures here instead. In a dialog the strip
            above the fields already carries them, so this was the same three
            numbers twice, two hundred pixels apart. */}
        {isDetailsVisible && !asDialog && (
          <PageDetails>
            <ValueDetails
              balance={data?.totalCash}
              reserved={data?.reservedCash}
            />
          </PageDetails>
        )}

        <Form amount={amount} total={total} newTotal={newTotal}>
          <form id={WITHDRAW_FORM_ID} onSubmit={formik.handleSubmit}>
            <Form.Element>
              <AccountRow>
                <AccountField>
                  <Select
                    label="Your Bank Account"
                    required
                    onBlur={formik.handleBlur}
                    error={feedback("destination")}
                    name="destination"
                    id="destination"
                    onSelectionChange={(value, name) => {
                      formik.setFieldValue(name, value);
                      const provider =
                        (bankAccounts ?? []).find((b) => b.value === value)
                          ?.provider || "manual";
                      formik.setFieldValue("provider", provider);
                    }}
                    size="large"
                    items={bankAccounts ?? []}
                    loading={isLoading}
                    disabled={isLoading}
                    value={formik.values.destination}
                  />
                </AccountField>

                {/* The deposit dialog offers this beside the same field; the
                    withdraw dialog sent a reader with one stale account off
                    to Settings to add another. */}
                <FDeposit.AddLink
                  icon={<HugeIcon icon={PlusSignCircleIcon} />}
                  onClick={() => setAddingBank(true)}
                >
                  New Bank Account
                </FDeposit.AddLink>
              </AccountRow>
            </Form.Element>
            <Form.Element>
              <Form.InputNumber
                id="amount"
                name="amount"
                required
                /* Nothing to type into it while the account holds less than
                   the smallest withdrawal: the note above says why, and an
                   editable field under that note invites a figure that
                   cannot be accepted whatever it is. */
                disabled={!canWithdraw}
                error={feedback("amount")}
                /* Marked touched explicitly. Ant Design's number input does
                   not hand formik's blur handler a target it can read a name
                   off, so `touched.amount` never became true — the field was
                   invalid, the button was correctly disabled, and the reader
                   was told nothing about why. The deposit form already sets
                   it this way. */
                onBlur={() => {
                  formik.setFieldTouched("amount", true);
                }}
                formatter={(value) =>
                  `USD ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/USD\s?|(,*)/g, "")}
                placeholder="2000"
                precision={0}
                onChange={(value) => onSelectionChange(value, "amount")}
                label="Amount"
                value={formik.values.amount}
                color={theme.colors.primary}
              />
            </Form.Element>
            <Form.Element>
              <Form.TextArea
                label="Comment"
                onBlur={formik.handleBlur}
                error={feedback("comment")}
                name="comment"
                id="comment"
                placeholder="Add your comment here"
                onChange={({ target }) =>
                  onSelectionChange(target.value, "comment")
                }
                value={formik.values.comment}
              />
            </Form.Element>
            {/* In the dialog the pair goes to the shell's FOOT, from here —
                the flow keeps its own state and the actions land in the one
                band that cannot scroll. See ui/Modal's DialogFooter. */}
            <Slot asDialog={asDialog}>
              <Form.Element>
                <Form.Action $band={false}>
                  {/* In a dialog, Cancel CLOSES it. It pushed the dashboard
                    route instead — and the dialog is opened over the
                    dashboard, so the push was a no-op and the button did
                    nothing at all. The deposit dialog's Cancel already
                    branches this way. */}
                  <UIButton
                    variant="secondary"
                    size="lg"
                    onClick={() =>
                      asDialog ? onClose?.() : history.push(DASHBOARD)
                    }
                  >
                    Cancel
                  </UIButton>
                  <UIButton
                    variant="primary"
                    size="lg"
                    type="submit"
                    form={WITHDRAW_FORM_ID}
                    disabled={!isSubmitAllowed}
                    loading={
                      formik.isSubmitting || sendWithdrawOtpRequestIsLoading
                    }
                  >
                    Withdraw
                  </UIButton>
                </Form.Action>
              </Form.Element>
            </Slot>
          </form>
        </Form>
      </FWithdraw>

      <ManualBankModel
        visible={addingBank}
        client={clientData}
        /* Remounted per open, as the deposit dialog does it, so a half-filled
           form is not still there next time. */
        key={String(addingBank)}
        onCancel={() => {
          setAddingBank(false);
          /* The new account has to appear in the select it was added from. */
          refetchBanks();
        }}
      />
    </Withdraws>
  );

  const balanceStrip = (inDialog?: boolean) => (
    <ValueDetails
      balance={data?.totalCash}
      reserved={data?.reservedCash}
      /* Nothing is being withdrawn while nothing can be: the opening figure
         sits in a disabled field, and projecting it into "New balance"
         states a withdrawal the form will not take. */
      amount={canWithdraw ? formik.values.amount : 0}
      asDialog={inDialog}
      updating={(balancesFetching || banksFetching) && Boolean(data?.totalCash)}
    />
  );

  if (asDialog)
    return (
      <DialogBody>
        {balanceStrip(true)}
        {body}
      </DialogBody>
    );

  return (
    /* One block, not two flex items: the app's content shell is a flex row,
       so a header added as a sibling of the layout would make the layout a
       flex item beside it and both would shrink to their content. */
    <Page>
      <PageHeader
        name="Withdraw cash"
        lead="Send money from your FinaMaze balance back to your own bank."
      />
      <Layout customDetail={() => balanceStrip()}>
        <Wrapper>{body}</Wrapper>
      </Layout>
    </Page>
  );
};

export default WithdrawContainer;
